import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-full flex flex-col gap-4 text-left" }
const _hoisted_2 = { class: "w-full flex flex-wrap items-center justify-between gap-4" }
const _hoisted_3 = { class: "flex-grow min-w-[260px] sm:flex-grow-0 sm:w-2/5" }
const _hoisted_4 = { class: "flex-shrink-0" }
const _hoisted_5 = { class: "w-full h-full my-0" }

import { ref, watch, onMounted, computed } from 'vue'
// Group
import GroupTab from './group/group.tab.vue'
import CreateGroupModal from './group/modal/create.group.modal.vue'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import CreateUserModal from './components/CreateUserModal/Index.vue'
import UserTab from './user.tab.vue'
import SubjectPermissionsTreeModal from './components/PermissionsTreeModal/index.vue'

import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import { useGroupStore } from '@/pinia-store/useGroupStore'
import { useUserStore } from '@/pinia-store/useUsersStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useErrorToast } from '@/composables/useErrorToast'

type UsersTabValue = 'users' | 'groups'


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const modalStore = useModalStore()
const permissionsStore = usePermissionsStore()
const headerStore = useHeaderStore()

const showModalCreateUser = ref(false)
const textInput = ref('')

const activeTab = ref<UsersTabValue>('users')
const tabsName = ref<Record<UsersTabValue, string>>({ users: 'usuários', groups: 'grupos' })
const tabList: TabItem[] = [
  { label: 'Usuários', value: 'users', testId: 'users-tab-button' },
  { label: 'Grupos', value: 'groups', testId: 'groups-tab-button' }
]

const userStore = useUserStore()
const groupStore = useGroupStore()
const { notifyError } = useErrorToast()

const loadUsers = async () => {
  try {
    await userStore.fetchUsers()
  } catch (error) {
    notifyError(error, 'Não foi possível carregar os usuários.')
  }
}

const isLoading = computed(() => {
  if (activeTab.value === 'users') {
    return userStore.isLoading
  }
  if (activeTab.value === 'groups') {
    return groupStore.isLoading || false
  }
  return false
})

let debounceTimer: NodeJS.Timeout

const formatSearchText = (text: string): string => text.toLowerCase().trim()

watch(textInput, (searchText: string) => {
  clearTimeout(debounceTimer)

  debounceTimer = setTimeout(() => {
    const formattedText = formatSearchText(searchText)

    if (activeTab.value === 'users') {
      userStore.setSearchTerm(formattedText)
      loadUsers()
    } else if (activeTab.value === 'groups') {
      groupStore.applyFilters({ searchTerm: formattedText })
    }
  }, 500)
})

const handleSearchIconClick = () => {
  clearTimeout(debounceTimer)
  userStore.setSearchTerm(textInput.value)
  loadUsers()
}

watch(activeTab, (newTab: UsersTabValue) => {
  textInput.value = ''
  if (newTab === 'users') {
    userStore.setSearchTerm('')
    loadUsers()
  } else if (newTab === 'groups') {
    groupStore.applyFilters({ searchTerm: '' })
  }
})

const toggleCreateModal = () => {
  if (activeTab.value === 'users') {
    showModalCreateUser.value = !showModalCreateUser.value
    return
  }
  modalStore.toggleModal(EModals.CREATE_GROUP_MODAL)
}

const toggleModalCreateUser = () => {
  showModalCreateUser.value = !showModalCreateUser.value
}

onMounted(() => {
  headerStore.setTitle(
    'Usuários e Grupos',
    'Gerencie usuários, grupos e permissões de acesso ao sistema'
  )

  loadUsers()
})

const canUserCreating = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyUserCreating,
    resourceType: AuthorizationResourcesAndSubjects.Company
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(SearchInput, {
            modelValue: textInput.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((textInput).value = $event)),
            onSearchClick: handleSearchIconClick,
            width: "100%",
            loading: isLoading.value,
            label: `Buscar ${tabsName.value[activeTab.value]}`,
            "hide-details": "",
            density: "compact",
            spellcheck: "false",
            "data-testid": "users-search-input"
          }, null, 8, ["modelValue", "loading", "label"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(FilledButton, {
            onClick: toggleCreateModal,
            text: `Criar ${tabsName.value[activeTab.value]}`,
            width: "180px",
            height: "40px",
            disabled: !canUserCreating.value,
            class: "!bg-primary30",
            "data-testid": "create-user-button"
          }, null, 8, ["text", "disabled"])
        ])
      ]),
      _createElementVNode("div", null, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(Tabs, {
            modelValue: activeTab.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeTab).value = $event)),
            tabs: tabList
          }, {
            users: _withCtx(() => [
              _createVNode(UserTab)
            ]),
            groups: _withCtx(() => [
              _createVNode(GroupTab)
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(CreateUserModal, {
            onClose: toggleModalCreateUser,
            modalActive: showModalCreateUser.value
          }, null, 8, ["modalActive"])
        ])
      ])
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "#app" }, [
      _createVNode(CreateGroupModal),
      _createVNode(SubjectPermissionsTreeModal)
    ]))
  ], 64))
}
}

})